import React from "react";
import { Link } from "react-router-dom";
import useScrollReveal from "../../hooks/useScrollReveal";

export default function CapabilitiesSection() {
  const [ref, isVisible] = useScrollReveal();

  return (
    <section
      ref={ref}
      id="capabilities"
      className={`py-24 bg-white relative transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
        }`}
    >
      {/* Decorative background grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-brand-teal font-bold text-xs uppercase tracking-widest block">
            Capabilities
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-brand-blue tracking-tight">
            Our Core Competencies
          </h2>
          <div className="h-1 w-20 bg-brand-teal mx-auto rounded-full" />
          <p className="text-slate-500 text-sm md:text-base font-light leading-relaxed">
            Integrating cutting-edge scientific expertise with robust intelligence principles; to deliver validated, scalable drug formulations.
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Card 1: Research & Development */}
          <div className="group relative bg-[#F7F9FA] rounded-2xl p-8 lg:p-10 border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-brand-teal/30 hover:bg-white transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between">
            <div className="space-y-6">
              {/* Icon Container */}
              <div className="w-14 h-14 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal group-hover:bg-brand-teal group-hover:text-white transition-colors duration-300">
                <svg
                  className="w-8 h-8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* Lab flask icon */}
                  <path d="M6 3h12" />
                  <path d="M12 3v17" />
                  <path d="M10 6h4" />
                  <path d="M19 17l-5-10V3H10v4L5 17c-.5.8-.1 2 .8 2h12.4c.9 0 1.3-1.2.8-2z" />
                </svg>
              </div>

              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-brand-blue group-hover:text-brand-teal transition-colors duration-200">
                  Research & Development
                </h3>
                <p className="text-slate-600 text-sm md:text-base leading-relaxed font-light">
                  Our dedicated and experienced team supports our Partners by focusing on incremental innovation in product and process development, for drug delivery systems. <br /> With coordinates from our IP, Formulation, and Analytical teams, we ensure the timely and cost-effective development of Generic and Complex Generic products. We help our Partners redefine their portfolio with efficient formulation screening, to meet the Portfolio needs.
                </p>
              </div>
            </div>

            <div className="pt-8">
              <Link
                to="/ResearchAndDevlop"
                className="inline-flex items-center text-brand-blue font-bold text-sm hover:text-brand-teal transition-colors"
              >
                Explore R&D Services
                <svg className="w-4 h-4 ml-1.5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Card 2: Quality iN Design (QbD) */}
          <div className="group relative bg-[#F7F9FA] rounded-2xl p-8 lg:p-10 border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-brand-teal/30 hover:bg-white transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between">
            <div className="space-y-6">
              {/* Icon Container */}
              <div className="w-14 h-14 rounded-xl bg-brand-blue/10 flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors duration-300">
                <svg
                  className="w-8 h-8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* Molecule grid / target icon */}
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                  <path d="M12 6v12" />
                  <path d="M6 12h12" />
                  <circle cx="12" cy="12" r="3" fill="currentColor" className="opacity-20 group-hover:opacity-40" />
                </svg>
              </div>

              <div className="space-y-3">
                <h3 className="text-2xl font-bold text-brand-blue group-hover:text-brand-teal transition-colors duration-200">
                  Quality <span className="font-normal italic text-brand-teal">iN</span> Design
                </h3>
                <p className="text-slate-600 text-sm md:text-base leading-relaxed font-light">
                  We build in quality. Guided by our highly experienced technical team, we lay the design foundation of product development, ensuring stable formulations and highly scalable processes.
                </p>
                <p className="text-slate-600 text-sm md:text-base leading-relaxed font-light">
                  Our systematic, scientific approach to process selection, composition, and technology adaptation helps generic and innovator companies minimize operational risk. This ensures patients receive safe, clinically equivalent, and affordable healthcare solutions.
                </p>
              </div>
            </div>

            <div className="pt-8">
              <Link
                to="/Pipeline"
                className="inline-flex items-center text-brand-blue font-bold text-sm hover:text-brand-teal transition-colors"
              >
                Learn about Quality iN Design
                <svg className="w-4 h-4 ml-1.5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
